import { useEffect, useState } from "react";

import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";

interface UseMicrophoneActivityOptions {
  /** Threshold for audio activity detection (0-1). Default is 0.01 */
  threshold?: number;
  enabled?: boolean;
}

/**
 * Hook to detect microphone activity using Web Audio API
 * @param options Configuration options for microphone activity detection
 * @returns Object containing isActive (boolean) indicating if microphone is detecting sound above threshold
 */
export function useMicrophoneActivity(
  options: UseMicrophoneActivityOptions = {},
) {
  const [isActive, setIsActive] = useState(false);
  const isMicrophoneMuted = useVidyoStore(
    (state) => state.microphone.shouldBeMuted,
  );
  const selectedMicrophone = useVidyoStore(
    (state) => state.microphone.selected,
  );

  useEffect(() => {
    if (isMicrophoneMuted || !selectedMicrophone || !options.enabled) {
      setIsActive(false);
      return;
    }

    let audioContext: AudioContext | undefined;
    let analyser: AnalyserNode | undefined;
    let microphone: MediaStreamAudioSourceNode | undefined;
    let mediaStream: MediaStream | undefined;
    let animationFrame: number;

    const threshold = options.threshold ?? 0.01;

    const detectSound = () => {
      if (!analyser) return;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(dataArray);

      // Calculate average volume level
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
      const normalizedAverage = average / 255; // Normalize to 0-1 range

      setIsActive(normalizedAverage > threshold);
    };

    const startDetection = async () => {
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        audioContext = new AudioContext();
        analyser = audioContext.createAnalyser();
        microphone = audioContext.createMediaStreamSource(mediaStream);

        microphone.connect(analyser);
        analyser.smoothingTimeConstant = 0.3;
        analyser.fftSize = 1024;

        const checkLevel = () => {
          detectSound();
          animationFrame = requestAnimationFrame(checkLevel);
        };

        checkLevel();
      } catch (error) {
        console.error("Error accessing microphone:", error);
      }
    };

    startDetection();

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
      if (microphone) {
        microphone.disconnect();
      }
      if (audioContext) {
        audioContext.close();
      }
      if (mediaStream) {
        mediaStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [
    isMicrophoneMuted,
    selectedMicrophone,
    options.threshold,
    options.enabled,
  ]);

  return { isActive };
}
